import {
  Path,
  Door,
  HandWaving,
  BookOpen,
  ChatCircleDots,
  Confetti,
  ArrowsClockwise,
  Bell,
  CheckCircle,
  ChartLineUp,
  Lightbulb,
  HandTap,
} from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../../library/LibraryPrimitives";
import { Flow } from "../diagrams";

const DAILY = [
  { icon: Door, label: "Open Bloom", desc: "A calm, familiar welcome", tone: "blue" },
  { icon: HandWaving, label: "Greeting + streak", desc: "“Hi Maya! 5-day streak”", tone: "orange" },
  { icon: HandTap, label: "Continue or challenge", desc: "One clear thing to do next", tone: "teal" },
  { icon: Confetti, label: "Celebrate", desc: "Earn XP, keep the streak", tone: "yellow" },
  { icon: ArrowsClockwise, label: "Come back tomorrow", desc: "Gentle habit, never nagging", tone: "blue" },
];

const LESSON = [
  { icon: BookOpen, label: "Pick a topic", desc: "From Learn or Continue", tone: "blue" },
  { icon: HandTap, label: "Listen & interact", desc: "Answer by tapping/choosing", tone: "orange" },
  { icon: ChatCircleDots, label: "Feedback / hint", desc: "Kind nudge, never punishing", tone: "teal" },
  { icon: Confetti, label: "Celebrate", desc: "Small win, big confidence", tone: "yellow" },
  { icon: ChartLineUp, label: "Progress grows", desc: "Mastery moves up a stage", tone: "blue" },
];

const HOMEWORK = [
  { icon: Bell, label: "Gentle reminder", desc: "“Homework due today”", tone: "orange" },
  { icon: HandTap, label: "Complete the task", desc: "Same friendly question flow", tone: "teal" },
  { icon: CheckCircle, label: "Score + feedback", desc: "Supportive, with retry offered", tone: "blue" },
  { icon: ChartLineUp, label: "Joins the journey", desc: "Saved to history & progress", tone: "yellow" },
];

export function JourneysSection() {
  return (
    <SectionShell
      id="journeys"
      eyebrow="User Journeys"
      eyebrowIcon={Path}
      title="How children move, naturally"
      description="Three core loops power everyday use. Each is short, satisfying and circular — finishing one thing gently invites the next, building a calm daily habit."
    >
      <div className="space-y-6">
        <Showcase title="Daily habit loop" description="The heartbeat of Bloom">
          <Flow steps={DAILY} />
        </Showcase>
        <Showcase title="Lesson loop" description="Listen → Interact → Feedback → Celebrate → Grow">
          <Flow steps={LESSON} />
        </Showcase>
        <Showcase title="Homework loop" description="From reminder to remembered milestone">
          <Flow steps={HOMEWORK} />
        </Showcase>

        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: HandTap, t: "Always a next step", d: "Every screen offers one clear, friendly action." },
            { icon: Lightbulb, t: "No dead ends", d: "Stuck? A hint, a retry, or a gentle route home." },
            { icon: ArrowsClockwise, t: "Loops, not lines", d: "Finishing invites the next small win — habit by design." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-3xl border border-border bg-card p-5 shadow-soft">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong"><Icon size={22} weight="fill" /></span>
              <p className="mt-3 font-display font-extrabold">{t}</p>
              <p className="mt-1 text-sm font-medium leading-relaxed text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

export default JourneysSection;
